import clsx from "clsx";
import _ from "lodash";
import { Plus, Trash, Pencil, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Select from "react-select";

import { Badge } from "@/design-system/components/Badge/Badge";
import { toast } from "@/design-system/components/Toast/Toast";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";

import Icons8CurlyBrackets from "@/components/icons/svg/windows-10/Icons8CurlyBrackets";
import Icons8Rename from "@/components/icons/svg/windows-10/Icons8Rename";
import {
  SortActiveDesc,
  SortInactive,
} from "@/components/icons/svg/figma/Sort";
import { DestinationKey } from "@/destinations/types";
import { isServerSideSource } from "@/eventTypes";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { beautifyBuiltinPropName, makeRequest } from "@/util";
import {
  TransformDefinition,
  TransformDefinitionSet,
} from "../transformationDefinition";
import {
  TransformationOperationType,
  TransformationPageSort,
  TransformationPageSortColumns,
  transformOperationOptions as operationOptions,
  TransformOperationOptionType as OperationOptionType,
  TypeSelectorOptions as castPropertyValueOptions,
} from "../types";
import { AvailableTransformsSelector } from "./AvailableTransformsSelector";
import { BeforeAndAfterComparison } from "./BeforeAndAfterEventComparison";
import { SendBuiltinPropertyInputs } from "./SendBuiltInPropertyInputs";
import {
  getSendConstPropTargetValue,
  initSendConstPropInputState,
  SendConstantPropertyInputs,
} from "./SendConstantPropertyInputs";
import { SQLEditor } from "./SQLEditor";
import { TransformationDeleteModal } from "./TransformationDeleteModal";
import { themedSelectStyles } from "@/components/Dropdown/themedSelectStyles";

const transformationListItemClass =
  "[&_code]:transition-all [&_code]:duration-500 [&_code]:max-w-[180px] [&_code]:overflow-hidden [&_code]:text-ellipsis [&_code]:whitespace-nowrap [&_code]:align-middle [&_code]:inline-block";
const transformationListItemClosedClass =
  "hover:bg-surface-hover [&:hover_code]:!bg-surface-pressed";
const iconWrapperClass =
  "w-10 h-10 bg-status-info-tint text-status-info rounded-full mr-4 flex justify-center items-center shrink-0";
const iconClass = "text-[28px] align-middle fill-current";

export const SortableTableColumn: React.FC<{
  column: TransformationPageSortColumns;
  handleClick: (column: TransformationPageSortColumns) => void;
  currentSort: TransformationPageSort;
  text: string;
}> = ({ handleClick, currentSort, column, text }) => {
  const internalHandleClick = () => {
    handleClick(column);
  };

  const Icon = currentSort.column === column ? SortActiveDesc : SortInactive;
  const isAsc =
    currentSort.column === column && currentSort.direction === "asc";
  const iconRendered = (
    <Icon
      className={clsx("ml-2 fill-gray-600 align-middle", {
        "text-textSecondary": currentSort.column === column,
        "rotate-180": isAsc,
      })}
    />
  );

  return (
    <th
      scope="col"
      className="cursor-pointer text-left font-normal select-none"
      onClick={internalHandleClick}
    >
      <div className="group flex items-center p-4 whitespace-nowrap">
        {text} {iconRendered}
      </div>
    </th>
  );
};

const TableRowForTransformation: React.FC<{
  reloadAllTransforms: () => void;
  transformation: TransformDefinition;
  transformationSet: TransformDefinitionSet;
  isServerSideOnly: boolean;
  isEditable: boolean;
}> = ({
  reloadAllTransforms,
  transformation,
  transformationSet,
  isServerSideOnly,
  isEditable,
}) => {
  const TransformationIcon = transformation.operationIcon();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const rowOnClick = isEditable ? () => setIsEditing(true) : () => {};

  const confirmDeleteTransform = () => {
    setIsSaving(true);
    makeRequest<any>("delete_transformation", {
      id: transformation.ID,
      transformationSetID: transformationSet.id,
    })
      .then((_response) => {
        setIsEditing(false);
        setIsSaving(false);
        reloadAllTransforms();
        setDeleteModalOpen(false);
      })
      .catch((error: string) => {
        setDeleteModalOpen(false);
        toast.error("Something went wrong!");
        console.error(error);
        setIsSaving(false);
      });
  };

  const saveTransform: saveTransformFn = ({
    targetValue,
    operation,
    operandValue,
  }) => {
    setIsSaving(true);
    makeRequest<any>("save_transformation", {
      id: transformation.ID,
      transformationSetID: transformationSet.id,
      targetValue,
      operation,
      operandValue,
    })
      .then(() => {
        setIsEditing(false);
        setIsSaving(false);
        reloadAllTransforms();
      })
      .catch((error: string) => {
        toast.error("Something went wrong!");
        console.error(error);
        setIsEditing(false);
        setIsSaving(false);
      });
  };

  const deleteModal = (
    <TransformationDeleteModal
      header="Are you sure you want to delete this transformation?"
      isOpen={deleteModalOpen}
      onConfirm={confirmDeleteTransform}
      onClose={() => setDeleteModalOpen(false)}
    />
  );

  if (isEditing) {
    return (
      <>
        {deleteModal}
        <EditTransform
          transformationSet={transformationSet}
          editingID={transformation.ID}
          initialOperandValue={transformation.operation.operandValue}
          initialTargetValue={transformation.operation.targetValue}
          initialOperationValue={transformation.operation.operationType}
          cancel={() => setIsEditing(false)}
          isSaving={isSaving}
          isServerSideOnly={isServerSideOnly}
          save={saveTransform}
          onDelete={() => setDeleteModalOpen(true)}
        />
      </>
    );
  } else {
    return (
      <div
        onClick={rowOnClick}
        className={clsx(
          "group flex items-center justify-between",
          "flex w-full cursor-pointer items-center rounded px-6 py-4 transition",
          transformationListItemClass,
          transformationListItemClosedClass,
        )}
      >
        {deleteModal}
        <div className="flex items-center">
          <div className={iconWrapperClass}>
            <TransformationIcon className={iconClass} />
          </div>
          <div>
            {transformation.operationShortDisplay()}
            <span className="ml-2">
              {transformation.exampleResultDisplay(transformationSet)}
            </span>
          </div>
        </div>
        {isEditable && (
          <span className="text-iconDefault items-center justify-center opacity-0 transition-all duration-500 group-hover:opacity-100">
            <Pencil />
          </span>
        )}
      </div>
    );
  }
};

const EditTransform: React.FC<{
  transformationSet: TransformDefinitionSet;
  cancel: () => void;
  save: saveTransformFn;
  isSaving: boolean;
  isServerSideOnly: boolean;
  onDelete?: () => void;
  initialOperationValue?: TransformationOperationType | null;
  initialTargetValue?: string | null;
  initialOperandValue?: string | boolean | number | null;
  editingID?: string | null;
}> = ({
  transformationSet,
  cancel,
  save,
  isSaving,
  isServerSideOnly,
  onDelete,
  initialOperandValue,
  initialOperationValue,
  initialTargetValue,
  editingID,
}) => {
  const [operationValue, setOperationValue] =
    useState<OperationOptionType | null>(null);
  const [targetValue, setTargetValue] = useState("");
  const [operandValue, setOperandValue] = useState<string>("");
  const [operandValueForCasting, setOperandValueForCasting] = useState<{
    label: React.ReactNode;
    value: string;
  } | null>(null);

  const accountMetadata = useAccountMetadata();
  const [constStr, setConstStr] = useState("");
  const [constType, setConstType] = useState<{
    label: React.ReactNode;
    value: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [sqlEditorOpen, setSQLEditorOpen] = useState(false);

  const canUseSQLTransformation =
    !!accountMetadata.config.sqlTransformations ||
    !!accountMetadata.config.sqlTransformationsv2;

  useEffect(() => {
    // If initialized with values, set up the state correctly.
    if (initialOperationValue) {
      setOperationValue(
        _.find(
          operationOptions(true, isServerSideOnly, transformationSet.eventType),
          (opt) => opt.value === initialOperationValue,
        ) || null,
      );
    }
    if (initialTargetValue) {
      setTargetValue(initialTargetValue);
    }
    if (initialOperandValue) {
      setOperandValue(initialOperandValue as string);
    }
    if (initialOperandValue && initialOperationValue === "castPropertyValue") {
      setOperandValueForCasting(
        _.find(
          castPropertyValueOptions,
          (opt) => opt.value === initialOperandValue,
        ) || null,
      );
    }

    if (
      initialTargetValue &&
      initialOperationValue === "sendConstantProperty"
    ) {
      initSendConstPropInputState(
        initialTargetValue,
        setConstStr,
        setConstType,
      );
    }
  }, [
    initialOperandValue,
    initialOperationValue,
    initialTargetValue,
    isServerSideOnly,
    transformationSet.eventType,
  ]);

  const isSendBuiltinProp =
    // @ts-ignore
    operationValue && operationValue.value === "sendBuiltinProperty";

  const isSendConstantProp =
    // @ts-ignore
    operationValue && operationValue.value === "sendConstantProperty";

  const showTargetValue =
    operationValue &&
    [
      "replacePropertyKey",
      "castPropertyValue",
      "uppercasePropertyValue",
      "lowercasePropertyValue",
      "deleteProperty",
      // @ts-ignore
    ].indexOf(operationValue.value) !== -1;

  const showOperandValue =
    operationValue &&
    [
      "replaceName",
      "replacePropertyKey",
      // @ts-ignore
    ].indexOf(operationValue.value) !== -1;

  const showCastSelect =
    // @ts-ignore
    operationValue && operationValue.value === "castPropertyValue";
  const showSQLEditButton =
    // @ts-ignore
    operationValue &&
    // @ts-ignore
    (operationValue.value === "sqlTransformation" ||
      // @ts-ignore
      operationValue.value === "postgresSQLTransformation");

  const getTargetValueToSave = useCallback(() => {
    if (isSendConstantProp) {
      return getSendConstPropTargetValue(constStr, constType);
    }
    return targetValue || null;
  }, [constStr, constType, isSendConstantProp, targetValue]);

  const getOperandValueToSave = useCallback(() => {
    // @ts-ignore
    if (operationValue.value === "castPropertyValue") {
      if (operandValueForCasting) {
        // @ts-ignore
        return operandValueForCasting.value;
      } else {
        return null;
      }
    } else if (isSendBuiltinProp) {
      // if the operand value isn't supplied, we will use the cleaned-up version of target value instead
      if (operandValue) {
        return operandValue;
      } else {
        var targetValue = getTargetValueToSave();
        if (targetValue && targetValue.startsWith("$")) {
          targetValue = targetValue.slice(1);
        }
        return targetValue;
      }
    }
    return operandValue || null;
  }, [
    getTargetValueToSave,
    isSendBuiltinProp,
    operandValue,
    operandValueForCasting,
    operationValue?.value,
  ]);

  const saveHandler = () => {
    setSaving(true);
  };

  useEffect(() => {
    if (!saving) {
      return;
    }

    if (!operationValue) {
      return;
    }

    const targetValue = getTargetValueToSave();
    const operandValue = getOperandValueToSave();
    if (isSendBuiltinProp && !(targetValue && operandValue)) {
      return;
    }

    save({
      operandValue,
      targetValue,
      // @ts-ignore
      operation: operationValue.value,
    });
    setOperationValue(null);
    setTargetValue("");
    setOperandValue("");
    setOperandValueForCasting(null);
    setConstStr("");
    setConstType(null);
    setSaving(false);
  }, [
    operationValue,
    targetValue,
    operandValue,
    isSendBuiltinProp,
    saving,
    getTargetValueToSave,
    getOperandValueToSave,
    save,
  ]);

  return (
    <div
      className={clsx("my-2 rounded-lg border px-6 pt-2 pb-4", {
        "opacity-50": isSaving,
      })}
    >
      {sqlEditorOpen && (
        <SQLEditor
          onClose={() => setSQLEditorOpen(false)}
          initialQuery={operandValue}
          queryType={operationValue}
          onSave={(query: string) => {
            setOperandValue(query);
            setSQLEditorOpen(false);
            saveHandler();
          }}
        />
      )}
      <div className="text-md text-textDefault mb-4 flex items-center justify-between font-bold">
        {editingID && <span>Edit transformation</span>}
        {!editingID && <span>Add new transformation</span>}
        <Tooltip content="Cancel">
          <span
            onClick={cancel}
            className="text-textError hover:bg-status-error-tint hover:text-status-error flex h-8 w-8 cursor-pointer items-center justify-center rounded-full"
          >
            {editingID ? <X size={20} /> : <Trash size={20} />}
          </span>
        </Tooltip>
      </div>

      <AvailableTransformsSelector
        canUseSQLTransformation={canUseSQLTransformation}
        isServerSideOnly={isServerSideOnly}
        transformationOption={operationValue}
        setTransformationOption={setOperationValue}
        eventType={transformationSet.eventType}
      />

      {showTargetValue && (
        <input
          placeholder={
            // @ts-ignore
            operationValue && operationValue.value === "replaceName"
              ? "Original Event Name..."
              : "Original Property..."
          }
          className="mt-2 w-full rounded border px-2 py-2 font-mono tracking-tight"
          value={targetValue}
          onChange={(e) => setTargetValue(e.target.value)}
        />
      )}

      {showOperandValue && (
        <input
          className="mt-2 w-full rounded border px-2 py-2 font-mono tracking-tight"
          placeholder="Rename to..."
          value={operandValue}
          onChange={(e) => setOperandValue(e.target.value)}
        />
      )}

      {/* @ts-ignore */}
      {showCastSelect && (
        <Select
          styles={themedSelectStyles<{
            label: React.ReactNode;
            value: string;
          }>()}
          className="mt-2"
          placeholder="Change to..."
          value={operandValueForCasting}
          options={castPropertyValueOptions}
          onChange={setOperandValueForCasting}
        />
      )}

      {isSendBuiltinProp && (
        <SendBuiltinPropertyInputs
          originalBuiltinPropName={targetValue}
          setOriginalBuiltinPropName={setTargetValue}
          newPropName={operandValue}
          setNewPropName={setOperandValue}
        />
      )}

      {isSendConstantProp && (
        <SendConstantPropertyInputs
          constStr={constStr}
          setConstStr={setConstStr}
          constType={constType}
          setConstType={setConstType}
          operandValue={operandValue}
          setOperandValue={setOperandValue}
        />
      )}

      <div className="mt-2 flex items-center justify-between">
        {editingID && (
          <>
            <button
              className="btn bg-status-error-tint text-status-error hover:bg-status-error/20 w-1/3 rounded py-2 text-center"
              onClick={onDelete}
            >
              <Trash
                style={{ position: "relative", top: -1 }}
                className="mr-1"
                size="16"
              />
              Delete
            </button>
            <div className="w-4 flex-shrink-0 flex-grow-0" />
          </>
        )}
        {showSQLEditButton ? (
          <button
            onClick={() => setSQLEditorOpen(true)}
            className="btn bg-surface-subtle text-textActionPressed hover:bg-interactive-selected/20 w-full rounded py-2 text-center"
          >
            Edit SQL Query
          </button>
        ) : (
          <button
            className="btn bg-surface-subtle text-textActionPressed hover:bg-interactive-selected/20 w-full rounded py-2 text-center"
            onClick={saveHandler}
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
};

type saveTransformFn = ({
  targetValue,
  operation,
  operandValue,
}: {
  targetValue: string | null;
  operation: string | null;
  operandValue: string | null;
}) => void;

export function isTransformationSetServerSideOnly(
  transformationSet: TransformDefinitionSet,
  serverDestinationKeys: Partial<Record<DestinationKey, string>>,
): boolean {
  return (
    (transformationSet.destination !== null &&
      transformationSet.destination.key in serverDestinationKeys) ||
    (transformationSet.eventDefId !== null &&
      isServerSideSource(transformationSet.eventDefEventSource))
  );
}

/** The transformations in a set, with add, edit, and delete controls and a before-and-after preview. */
export const TransformationSetEditor: React.FC<{
  transformationSet: TransformDefinitionSet;
  isServerSideOnly: boolean;
  isEditable: boolean;
  reloadAllTransforms: () => void;
  layout?: "split" | "stacked";
}> = ({
  transformationSet,
  isServerSideOnly,
  isEditable,
  reloadAllTransforms,
  layout = "split",
}) => {
  const [addingNew, setAddingNew] = useState(
    isEditable && transformationSet.transformations.length === 0,
  );
  const [isSaving, setIsSaving] = useState(false);
  const sampleEvent = useMemo(
    () => transformationSet.getSampleInputEvent(),
    [transformationSet],
  );
  const output = useMemo(
    () =>
      transformationSet.perform(
        sampleEvent.eventName,
        sampleEvent.eventProperties,
      ),
    [transformationSet, sampleEvent],
  );

  const saveNewTransform: saveTransformFn = ({
    targetValue,
    operation,
    operandValue,
  }) => {
    setIsSaving(true);
    makeRequest<any>("save_transformation", {
      targetValue: targetValue,
      operation: operation,
      operandValue: operandValue,
      transformationSetID: transformationSet.id,
    })
      .then((_response) => {
        setIsSaving(false);
        setAddingNew(false);
        reloadAllTransforms();
      })
      .catch((error: string) => {
        toast.error("Something went wrong!");
        console.error(error);
        setIsSaving(false);
      });
  };

  const stacked = layout === "stacked";

  return (
    <div className={clsx("flex", stacked ? "flex-col gap-6" : "items-start")}>
      <div className={clsx("flex flex-col", !stacked && "w-1/3")}>
        {transformationSet
          .orderedTransformations()
          .map((transformation, index) => (
            <TableRowForTransformation
              reloadAllTransforms={reloadAllTransforms}
              key={transformation.ID ?? index}
              transformation={transformation}
              transformationSet={transformationSet}
              isServerSideOnly={isServerSideOnly}
              isEditable={isEditable}
            />
          ))}
        {transformationSet.transformations.length === 0 && !addingNew && (
          <div className="text-textSubtle mb-4 text-center font-bold">
            No transformations
          </div>
        )}
        {addingNew ? (
          <EditTransform
            transformationSet={transformationSet}
            isServerSideOnly={isServerSideOnly}
            isSaving={isSaving}
            cancel={() => setAddingNew(false)}
            save={saveNewTransform}
          />
        ) : (
          isEditable && (
            <button
              type="button"
              aria-label="Add a transformation"
              onClick={() => setAddingNew(true)}
              className={clsx(
                "bg-surface-default hover:bg-surface-selected flex w-full cursor-pointer items-center rounded px-6 py-4 transition-all duration-500",
                transformationListItemClass,
              )}
            >
              <div className={iconWrapperClass}>
                <Plus className={iconClass} />
              </div>
            </button>
          )
        )}
      </div>
      <div className={clsx("flex justify-center", !stacked && "w-2/3")}>
        <BeforeAndAfterComparison
          before={sampleEvent}
          after={output}
          eventType={transformationSet.eventType}
        />
      </div>
    </div>
  );
};

export const TransformationSetDescription: React.FC<{
  transformationSet: TransformDefinitionSet;
}> = ({ transformationSet }) => {
  if (transformationSet.description) {
    return <span>{transformationSet.description}</span>;
  } else {
    const { propertyTransformations, eventTransformations } =
      transformationSet.operationSummary();
    return (
      <div
        className={clsx(
          "text-textSecondary text-xs",
          transformationListItemClass,
        )}
      >
        {eventTransformations.count ? (
          <div className="mb-1 flex items-center">
            <Icons8Rename className="text-textSubtle mr-1" />
            Rename to{" "}
            <code className="mx-1 bg-gray-400 text-xs">
              {eventTransformations.sample.operation.operandValue}
            </code>
          </div>
        ) : null}
        {propertyTransformations.count ? (
          <div className="mb-1 flex items-center">
            <Icons8CurlyBrackets className="text-textSubtle mr-1" />
            {propertyTransformations.count === 1 ? (
              <>
                {propertyTransformations.sample.operation.shortName()}&nbsp;
                {propertyTransformations.sample.operation.operationType ===
                  "sendBuiltinProperty" && (
                  <code className="mx-1 bg-gray-400 text-xs">
                    {propertyTransformations.sample.operation.targetValue
                      ? beautifyBuiltinPropName(
                          propertyTransformations.sample.operation.targetValue,
                        )
                      : propertyTransformations.sample.operation.targetValue}
                  </code>
                )}
                {propertyTransformations.sample.operation.operandValue !==
                  null &&
                  propertyTransformations.sample.operation.operationType !==
                    "sqlTransformation" &&
                  propertyTransformations.sample.operation.operationType !==
                    "postgresSQLTransformation" && (
                    <>
                      to
                      <Tooltip
                        content={
                          propertyTransformations.sample.operation.operandValue
                        }
                      >
                        <code className="mx-1 bg-gray-400 text-xs">
                          {
                            propertyTransformations.sample.operation
                              .operandValue
                          }
                        </code>
                      </Tooltip>
                    </>
                  )}
              </>
            ) : (
              <>{propertyTransformations.count} property transformations</>
            )}
          </div>
        ) : null}
      </div>
    );
  }
};

export const TableRowForTransformationSet: React.FC<{
  transformationSet: TransformDefinitionSet;
  reloadAllTransforms: () => void;
  availableServerDestinations: Record<DestinationKey, string>;
  isEditable: boolean;
  isOpen: boolean;
  onOpen: (id: string) => void;
}> = ({
  reloadAllTransforms,
  transformationSet,
  isEditable,
  isOpen,
  onOpen,
  availableServerDestinations,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const Icon = transformationSet.icon();
  const icon = <Icon className="mr-1.5 fill-current text-[18px]" />;
  const DestIcon = transformationSet.destinationIcon();
  const SourceIcon = transformationSet.eventSourceIcon();
  const [, setIsSaving] = useState(false);

  const handleOnOpen = () => {
    if (isOpen) {
      onOpen("");
    } else {
      onOpen(transformationSet.id);
    }
  };

  const confirmDeleteTransform = () => {
    setIsSaving(true);
    setDeleteModalOpen(false);
    makeRequest<any>("delete_transformation_set", {
      id: transformationSet.id,
    })
      .then((_response) => {
        setIsSaving(false);
        reloadAllTransforms();
      })
      .catch((error: string) => {
        toast.error("Something went wrong!");
        console.error(error);
        setIsSaving(false);
      });
  };

  const startDelete = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setDeleteModalOpen(true);
    event.stopPropagation();
  };

  return (
    <>
      <TransformationDeleteModal
        header="Are you sure you want to delete this transformation set?"
        body="This action is irreversible."
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteTransform}
        onClose={() => setDeleteModalOpen(false)}
      />
      <tr
        className={clsx("hover:bg-surface-hover cursor-pointer", {
          "bg-surface-muted border-b-0": isOpen,
        })}
        onClick={handleOnOpen}
      >
        <td className="border-0 p-4">
          <Badge
            variant="success"
            className="whitespace-nowrap"
            text={
              <>
                {icon}
                {transformationSet.intentionDisplay()}
              </>
            }
          />
        </td>
        <td className="border-0 p-4">
          <TransformationSetDescription transformationSet={transformationSet} />
        </td>
        <td className="border-0 p-4">
          {SourceIcon ? (
            <Tooltip content={transformationSet.eventSourceDisplay()}>
              <SourceIcon className="mr-1 fill-gray-600 align-middle text-xl" />
            </Tooltip>
          ) : null}{" "}
          {transformationSet.eventDefNameDisplay()}
        </td>
        <td className="border-0 p-4">
          {DestIcon ? (
            <Tooltip content={transformationSet.destinationDisplay()}>
              <DestIcon className="h-[30px] w-[30px] text-center align-middle" />
            </Tooltip>
          ) : (
            transformationSet.destinationDisplay()
          )}
        </td>
        <td className="border-0 p-4">
          {new Date(transformationSet.createdAt as string).toLocaleDateString()}
        </td>
        {isEditable && (
          <td className="border-0 p-4">
            <div className="flex justify-center">
              <div
                onClick={startDelete}
                className="text-textError hover:bg-status-error-tint hover:text-status-error flex h-12 w-12 cursor-pointer items-center justify-center rounded-full p-2 text-2xl"
              >
                <Trash />
              </div>
            </div>
          </td>
        )}
      </tr>
      {isOpen && (
        <tr>
          <td className="border-0 p-4 align-top" colSpan={9999}>
            <TransformationSetEditor
              transformationSet={transformationSet}
              isServerSideOnly={isTransformationSetServerSideOnly(
                transformationSet,
                availableServerDestinations,
              )}
              isEditable={isEditable}
              reloadAllTransforms={reloadAllTransforms}
            />
          </td>
        </tr>
      )}
    </>
  );
};
